/**
 * SyncSettings.jsx — Controlo de sincronização com o Microsoft Planner.
 * Permite ver estado, alterar frequência, forçar sync e ver log.
 */

import React, { useEffect, useState } from "react";
import { fetchHealth, fetchSyncLog, forcaSync, fetchConfiguracoes, updateConfiguracao } from "../../services/api";

export default function SyncSettings() {
  const [health, setHealth] = useState(null);
  const [log, setLog] = useState([]);
  const [intervalo, setIntervalo] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncEmCurso, setSyncEmCurso] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const carregar = async () => {
    setLoading(true);
    try {
      const [h, l, configs] = await Promise.all([fetchHealth(), fetchSyncLog(), fetchConfiguracoes()]);
      setHealth(h);
      setLog(l);
      const conf = configs.find((c) => c.chave === "sync_intervalo_horas");
      if (conf) setIntervalo(conf.valor);
    } catch (e) {
      setFeedback({ tipo: "erro", msg: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); }, []); // eslint-disable-line

  const executarSync = async () => {
    setSyncEmCurso(true);
    setFeedback(null);
    try {
      const resultado = await forcaSync();
      setFeedback({
        tipo: "ok",
        msg: `Sync concluído: ${resultado.planners_sync} planners, ${resultado.tarefas_sync} tarefas. Estado: ${resultado.estado}`,
      });
      carregar();
    } catch (e) {
      setFeedback({ tipo: "erro", msg: e.message });
    } finally {
      setSyncEmCurso(false);
    }
  };

  const guardarIntervalo = async () => {
    try {
      await updateConfiguracao("sync_intervalo_horas", intervalo);
      setFeedback({ tipo: "ok", msg: "Intervalo de sync actualizado." });
    } catch (e) {
      setFeedback({ tipo: "erro", msg: e.message });
    }
  };

  if (loading) return <p style={styles.loading}>A carregar…</p>;

  return (
    <div>
      {/* Estado atual */}
      {health && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitulo}>Estado Actual</h3>
          <div style={styles.estadoGrid}>
            <div style={styles.estadoItem}>
              <span style={styles.estadoLabel}>Azure AD</span>
              <span style={{ ...styles.badge, ...(health.azure_configurado ? styles.badgeOk : styles.badgeAlerta) }}>
                {health.azure_configurado ? "Configurado" : "Não configurado"}
              </span>
            </div>
            <div style={styles.estadoItem}>
              <span style={styles.estadoLabel}>Planners sincronizados</span>
              <span style={styles.estadoValor}>{health.base_dados.total_planners}</span>
            </div>
            <div style={styles.estadoItem}>
              <span style={styles.estadoLabel}>Total tarefas</span>
              <span style={styles.estadoValor}>{health.base_dados.total_tarefas}</span>
            </div>
            {health.ultimo_sync && (
              <div style={styles.estadoItem}>
                <span style={styles.estadoLabel}>Último sync</span>
                <span style={styles.estadoValor}>{health.ultimo_sync.iniciado_em?.slice(0, 16).replace("T", " ")}</span>
              </div>
            )}
          </div>

          <button
            style={{ ...styles.btnSync, opacity: syncEmCurso ? 0.6 : 1 }}
            onClick={executarSync}
            disabled={syncEmCurso}
          >
            {syncEmCurso ? "A sincronizar…" : "🔄 Sincronizar Agora"}
          </button>
        </div>
      )}

      {/* Frequência */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitulo}>Frequência de Sincronização Automática</h3>
        <div style={styles.inputRow}>
          <input
            style={styles.input}
            type="number"
            min="1"
            max="24"
            value={intervalo}
            onChange={(e) => setIntervalo(e.target.value)}
          />
          <span style={styles.unidade}>horas</span>
          <button style={styles.btn} onClick={guardarIntervalo}>Guardar</button>
        </div>
      </div>

      {feedback && (
        <p style={feedback.tipo === "ok" ? styles.ok : styles.erro}>{feedback.msg}</p>
      )}

      {/* Log */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitulo}>Últimas 10 Sincronizações</h3>
        {log.length === 0 ? (
          <p style={styles.semDados}>Nenhuma sincronização registada.</p>
        ) : (
          <table style={styles.tabela}>
            <thead>
              <tr>
                <th style={styles.th}>Data</th>
                <th style={styles.th}>Estado</th>
                <th style={styles.th}>Planners</th>
                <th style={styles.th}>Tarefas</th>
                <th style={styles.th}>Erros</th>
              </tr>
            </thead>
            <tbody>
              {log.map((row) => (
                <tr key={row.id}>
                  <td style={styles.td}>{row.iniciado_em?.slice(0, 16).replace("T", " ")}</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.badge, ...(row.estado === "sucesso" ? styles.badgeOk : row.estado === "em_curso" ? styles.badgeInfo : styles.badgeErro) }}>
                      {row.estado}
                    </span>
                  </td>
                  <td style={styles.td}>{row.planners_sync ?? 0}</td>
                  <td style={styles.td}>{row.tarefas_sync ?? 0}</td>
                  <td style={styles.td}>{row.erros ? row.erros.length : 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const styles = {
  section: { background: "#fff", borderRadius: 8, padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,.08)", marginBottom: 20 },
  sectionTitulo: { fontSize: 15, fontWeight: 600, margin: "0 0 16px", color: "#1e293b" },
  estadoGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 },
  estadoItem: { display: "flex", flexDirection: "column", gap: 4 },
  estadoLabel: { fontSize: 12, color: "#64748b" },
  estadoValor: { fontSize: 20, fontWeight: 700, color: "#1e293b" },
  badge: { display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600 },
  badgeOk: { backgroundColor: "#dcfce7", color: "#166534" },
  badgeAlerta: { backgroundColor: "#fef3c7", color: "#92400e" },
  badgeErro: { backgroundColor: "#fee2e2", color: "#991b1b" },
  badgeInfo: { backgroundColor: "#e0f2fe", color: "#075985" },
  btnSync: { padding: "10px 24px", backgroundColor: "#1e293b", color: "#fff", border: "none", borderRadius: 6, fontSize: 14, cursor: "pointer", fontWeight: 600 },
  inputRow: { display: "flex", alignItems: "center", gap: 10 },
  input: { width: 72, padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 16, fontWeight: 700, textAlign: "center" },
  unidade: { fontSize: 14, color: "#64748b" },
  btn: { padding: "8px 20px", backgroundColor: "#6366f1", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, cursor: "pointer", fontWeight: 600 },
  tabela: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "8px 12px", backgroundColor: "#f8fafc", fontWeight: 600, borderBottom: "2px solid #e2e8f0", color: "#475569" },
  td: { padding: "8px 12px", color: "#374151", borderBottom: "1px solid #f1f5f9" },
  semDados: { color: "#94a3b8", fontSize: 14, margin: 0 },
  ok: { fontSize: 13, color: "#16a34a", marginTop: 8 },
  erro: { fontSize: 13, color: "#dc2626", marginTop: 8 },
  loading: { color: "#64748b" },
};
